import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { DevTool } from "@hookform/devtools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { UseMutateFunction } from "react-query";
import { ZodSchema } from "zod";
import cx from "../../utils/cx.ts";
import { Maybe } from "../../utils/types/maybe.ts";
import ErrorModal from "../app/errorModal.tsx";
import Divider from "../divider.tsx";
import LoadingSpinner from "../loadingSpinner.tsx";
import PageHeader from "../pageHeader.tsx";
import FormErrorsModal from "./formErrorsModal.tsx";

type Props<T extends FieldValues> = {
  schema: ZodSchema;
  pageTitle?: Maybe<string>;
  defaultValues?: DefaultValues<T>;
  submitMutation?: Maybe<UseMutateFunction<T, unknown, T>>;
  onSave: (values: T) => void;
  onCancel?: () => void;
  renderForm: (
    form: UseFormReturn<T>,
    submit: () => Promise<void>,
  ) => JSX.Element;
  isSubmitting: boolean;
  formStyle?: "page" | "inline" | "no-submit";
  saveLabel?: string;
};

export default function PmtForm<T extends FieldValues>({
  schema,
  pageTitle,
  defaultValues,
  submitMutation,
  onSave,
  onCancel,
  renderForm,
  isSubmitting,
  formStyle = "page",
  saveLabel = "Save",
}: Props<T>) {
  const form = useForm<T>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });
  const { formState, getValues, control, trigger } = form;
  const { isValid, errors } = formState;

  const [isFormErrorModalOpen, setIsFormErrorModalOpen] = useState(false);

  const [isApiErrorModalOpen, setIsApiErrorModalOpen] = useState(false);
  const [apiError, setApiError] = useState<unknown>(null);

  const hasFormErrors = Object.keys(errors).length !== 0;

  const submit = async () => {
    if (!isValid) {
      await trigger();
      return;
    }

    const formValues = getValues();

    submitMutation
      ? submitMutation(formValues, {
          onSuccess: onSave,
          onError: (error) => {
            console.error("error", error);
            setApiError(error);
            setIsApiErrorModalOpen(true);
          },
        })
      : onSave(formValues);
  };

  return (
    <div className={cx(formStyle === "page" ? "h-screen overflow-y-auto" : "")}>
      {pageTitle != null && (
        <div className="sticky top-0 bg-white z-20 pt-6">
          <div className="px-12">
            <PageHeader title={pageTitle} />
          </div>
          <Divider />
        </div>
      )}
      <FormProvider {...form}>
        <form className="mt-6 px-12">
          <div className="space-y-12"></div>
          {renderForm(form, submit)}
        </form>
        <FormErrorsModal
          isOpen={isFormErrorModalOpen}
          onClose={() => setIsFormErrorModalOpen(false)}
          errors={errors}
        />
        <ErrorModal
          isOpen={isApiErrorModalOpen}
          onClose={() => setIsApiErrorModalOpen(false)}
          error={apiError}
        />
      </FormProvider>
      {formStyle !== "no-submit" && (
        <div
          className={cx(
            formStyle === "page" ? "absolute bottom-0 right-0 h-16 w-16" : "",
            "mt-10 flex items-center justify-end gap-x-6",
          )}
        >
          <div className="flex gap-4 bg-white z-20 mr-4 pr-12 pl-2 pt-2 pb-12">
            {onCancel != null && (
              <button
                type="button"
                className="text-sm font-semibold leading-6 text-gray-900"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={submit}
              className="rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
            >
              {saveLabel}
            </button>
            {isSubmitting && (
              <div className="flex items-center justify-center">
                <LoadingSpinner />
              </div>
            )}
            {hasFormErrors && (
              <button
                type="button"
                onClick={async () => setIsFormErrorModalOpen(true)}
                className="text-red-500 w-2 pl-1 py-0 border-0 hover:border-0 focus:outline-0"
              >
                <div className="mx-auto flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                  <ExclamationTriangleIcon
                    className="h-5 w-5 text-red-600"
                    aria-hidden="true"
                  />
                </div>
              </button>
            )}
          </div>
        </div>
      )}
      {formStyle !== "no-submit" &&
        import.meta.env.VITE_REACT_HOOK_FORM_DEV_TOOLS_ENABLED === "true" && (
          <DevTool control={control} />
        )}
    </div>
  );
}
